/**
 * Errori delle rotte del motore in italiano semplice (08 sez. 2.4, 16 sez. 3.4). Modulo puro.
 * Codici = quelli del core (api/errors.ts: 12 sez. 3.3 + 16 sez. 3.4 + canale del plugin) piu' tre del
 * browser (rotta non pubblicata, rete, sessione). Un test confronta l'elenco con il core.
 * Mai codici HTTP, JSON o testi inglesi del negozio: il testo originale resta nel registro.
 */

const GENERIC = 'Si è verificato un errore imprevisto. Riprova tra qualche minuto.';

export const COMMERCE_ERROR_TEXT: Readonly<Record<string, string>> = Object.freeze({
  // 12 sez. 3.3
  not_allowed: 'Serve il permesso di gestione dell\'E-commerce. Chiedilo a un amministratore.',
  addon_missing: 'Serve l\'addon Magazzino + E-commerce per collegare un negozio.',
  engine_disabled: 'Il collegamento con i negozi online è spento in questo momento. Riprova più tardi.',
  invalid_domain: 'L\'indirizzo non è valido. Per Shopify usa l\'indirizzo che finisce con .myshopify.com.',
  site_unreachable: 'Il sito non risponde all\'indirizzo {url}. Controlla che sia online.',
  woocommerce_not_found: 'Su questo sito non troviamo WooCommerce.',
  https_required: 'Il sito non è in https: WooCommerce non permette il collegamento sicuro.',
  request_not_ready: 'Stiamo ancora preparando l\'accesso per il tuo negozio. Ti avvisiamo quando puoi continuare.',
  state_invalid: 'Il collegamento non è andato a buon fine. Riparti da Collega.',
  state_expired: 'Il tempo per completare il collegamento è scaduto. Riparti da Collega.',
  cookie_mismatch: 'Il collegamento è partito da un altro browser. Riparti da Collega su questo browser.',
  hmac_invalid: 'La risposta del negozio non ha superato il controllo di sicurezza. Riparti da Collega.',
  authorization_denied: 'Non hai dato l\'autorizzazione su {platform}. Senza non possiamo leggere il negozio.',
  scopes_missing: '{platform} non ha concesso tutti i permessi necessari. Ricollega il negozio e accettali tutti.',
  store_in_other_org: 'Questo negozio è già collegato a un\'altra azienda su Flusia. Se è un errore, scrivici.',
  store_already_connected: 'Questo negozio è già collegato.',
  keys_not_working: 'Le chiavi del negozio non funzionano più. Ricollega il negozio.',
  permission_revoked: '{platform} non accetta più l\'accesso di Flusia. Ricollega il negozio.',
  internal_error: GENERIC,
  // 16 sez. 3.4
  token_not_allowed: 'Questa operazione si fa solo da Flusia, non con una chiave API.',
  not_found: 'Non troviamo questo elemento: forse è stato tolto nel frattempo.',
  invalid_state: 'Questa operazione non è possibile nello stato attuale del negozio.',
  job_already_running: 'C\'è già un lavoro in corso per questo negozio. Aspetta che finisca.',
  preview_required: 'Questa modifica sposta dei dati: prima va vista l\'anteprima dell\'effetto.',
  preview_stale: 'L\'anteprima non è più aggiornata: i dati sono cambiati. Ricalcola.',
  confirm_name_mismatch: 'Il nome scritto non corrisponde a quello del negozio.',
  gate_refused: 'Questa modifica non è consentita.',
  global_pause: 'Gli aggiornamenti verso i negozi sono fermi per manutenzione. Gli ordini continuano ad arrivare.',
  billing_suspended: 'Il collegamento è sospeso perché l\'addon non è attivo. Riattivalo per riprendere.',
  not_retryable: 'Questo elemento non si può riprovare.',
  not_undoable: 'Questo lavoro non si può annullare.',
  snapshot_not_ready: 'L\'istantanea non è ancora pronta.',
  restore_scope_invalid: 'Quello che hai scelto di ripristinare non è valido.',
  not_ready: 'Il file non è ancora pronto.',
  validation_failed: 'Alcuni dati inseriti non sono validi. Controllali e riprova.',
  rate_limited: 'Troppe richieste in poco tempo. Riprova tra poco.',
  // canale del plugin (18 sez. 4.3-4.4)
  clock_skew: 'L\'orologio del sito non è allineato: controlla data e ora del server WordPress.',
  key_revoked: 'La chiave del plugin è stata revocata. Abbina di nuovo il plugin a Flusia.',
  // solo browser
  route_not_found: 'Questa funzione non è ancora disponibile sul server di Flusia. Riprova più tardi.',
  network_error: 'Non riusciamo a raggiungere Flusia. Controlla la connessione e riprova.',
  not_authenticated: 'La sessione è scaduta. Accedi di nuovo e riprova.',
});

/** Un messaggio del core si mostra solo se e' italiano semplice (niente JSON, codici HTTP, inglese tecnico). */
function isPlainItalian(msg: string): boolean {
  if (msg.length === 0 || msg.length > 300) return false;
  if (/[{}[\]<>]|\bHTTP\b|\b[45]\d\d\b/.test(msg)) return false;
  return !/\b(error|failed|exception|not found|undefined|unauthorized|forbidden|authenticated|timeout|invalid)\b/i.test(msg);
}

export interface ErrorTextCtx {
  platform?: string | null;
  url?: string | null;
}

/**
 * invalid_domain per un sito WooCommerce: il testo generico parla di .myshopify.com. Il core lo da' anche per
 * i siti sui domini nostri (flusia.it, obly.it, obly.io), bloccati dalla protezione SSRF salvo eccezioni di Obly.
 */
const WOO_INVALID_DOMAIN = 'L\'indirizzo del sito non è valido o non è ammesso. Scrivi l\'indirizzo completo, per esempio https://www.tuosito.it. I siti ospitati da Obly si collegano solo dopo l\'abilitazione da parte nostra.';

export function commerceErrorText(code: string | null | undefined, message: string | null | undefined, ctx: ErrorTextCtx = {}): string {
  if (code === 'invalid_domain' && ctx.platform === 'WooCommerce') return WOO_INVALID_DOMAIN;
  const tpl = code ? COMMERCE_ERROR_TEXT[code] : undefined;
  if (tpl) {
    return tpl
      .replace(/\{platform\}/g, ctx.platform || 'Il negozio')
      .replace(/\{url\}/g, ctx.url || 'indicato');
  }
  if (message && isPlainItalian(message.trim())) return message.trim();
  return GENERIC;
}

/**
 * Codici per cui il core scrive gia' un testo italiano preciso: cancello rifiutato, stato non valido,
 * ripristino o annullo non possibili (dice perche'), scelte del collegamento non valide (dice quale).
 */
const CORE_WORDED: ReadonlySet<string> = new Set(['gate_refused', 'invalid_state', 'not_ready', 'not_undoable', 'restore_scope_invalid', 'validation_failed']);

/** Come commerceErrorText, ma per i cancelli preferisce la frase del core (dice quale cancello e perche'). */
export function commerceErrorTextDetailed(code: string | null | undefined, message: string | null | undefined, ctx: ErrorTextCtx = {}): string {
  if (code && CORE_WORDED.has(code) && message && isPlainItalian(message.trim())) return message.trim();
  return commerceErrorText(code, message, ctx);
}
